import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ConfigBundle } from '@cmp/contracts';
import { t } from '../../../lib/i18n';

export type Device = 'desktop' | 'tablet' | 'mobile';

/** Widths a customer's visitors actually use, not arbitrary breakpoints. */
export const DEVICES: Record<Device, { w: number; h: number; label: string }> = {
  desktop: { w: 1440, h: 900, label: 'Desktop 1440' },
  tablet: { w: 834, h: 1112, label: 'Tablet 834' },
  mobile: { w: 390, h: 844, label: 'Mobil 390' },
};

const UI_BUNDLE_URL = import.meta.env['VITE_UI_URL'] ?? 'http://127.0.0.1:58090/sdk/ui.js';

/**
 * Live preview of the banner (Doc 04 §5).
 *
 * This renders with the real ui.js, not a React re-creation of it. The alternative — an
 * approximation built from the same design tokens — is a second implementation of the
 * banner that has to be kept in step with the first, and the day it drifts is the day the
 * customer approves something that will not ship. The bundle likewise comes from the
 * server's own builder via /config/preview.
 *
 * ui.js only needs the session handoff, so the iframe provides a stub of it: enough
 * surface for the banner to render and respond to clicks, with no consent store, no
 * telemetry and no interception.
 */
export type PreviewView = 'banner' | 'preferences';

/**
 * "fit" scales the whole page down to the column; "full" renders at 1:1 and scrolls.
 *
 * Fit is the honest view of the composition and it is also the reason the banner used to
 * be unreadable: a 1440px page in a 912px column runs at 61%, so the banner's 13px body
 * text drew at 8px — the one thing being designed was the smallest thing on the screen.
 */
export type Zoom = 'fit' | 'full';

export function BannerFrame({
  bundle,
  device = 'desktop',
  view = 'banner',
  zoom = 'fit',
  backdrop,
  logo,
}: {
  bundle: ConfigBundle | null;
  device?: Device;
  view?: PreviewView;
  zoom?: Zoom;
  /**
   * A rendered screenshot of the customer's own page, as a data URL.
   *
   * A banner judged against grey placeholder bars is judged against nothing: customers
   * approve a design and then find it sitting on their own header, or disappearing into
   * their own palette. When this is absent the placeholder bars remain, which is honest
   * about being a stand-in.
   */
  backdrop?: string;
  /**
   * The site's logo, fetched through the API and inlined as a data URL.
   *
   * The frame is not the customer's page and cannot pretend to be one. A logo given as a
   * path — which is the recommended way to give it, because a site answers on several
   * hosts — resolves against the console here and 404s; an absolute one is refused by the
   * console's own `img-src 'self' data: blob:`, which an about:srcdoc document inherits.
   * Both showed the same broken-image icon in the one place the logo is chosen. Absent
   * leaves the bundle's own URL in place, which is right for a data: logo and no worse
   * than before for anything the proxy could not fetch.
   */
  logo?: string;
}) {
  const dim = DEVICES[device];
  const box = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState(0);

  // Measured, not guessed. A hardcoded width means the frame is clipped the moment the
  // column is narrower than the guess — and since the banner sits bottom-right, the
  // clipped part is exactly the part worth looking at.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setAvail(entry!.contentRect.width));
    ro.observe(el);
    setAvail(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [bundle]);
  const ref = useRef<HTMLIFrameElement>(null);
  const scroller = useRef<HTMLDivElement>(null);

  // A remounting iframe is white until it has parsed its document, and this one carries a
  // ~100 kB screenshot as a data URL. Saying so beats showing a white rectangle that reads
  // as a broken preview.
  const [painted, setPainted] = useState(false);

  // At 1:1 the interesting part of a 1440px page is wherever the banner sits, and that is
  // never the top-left corner the browser would scroll to. Derived from the layout the
  // bundle actually carries rather than assumed.
  const anchor = bundle?.ui.layout ?? 'box_bottom_right';
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || zoom !== 'full') return;
    const right = anchor.endsWith('_right');
    const bottom = anchor.startsWith('bar_bottom') || anchor.includes('_bottom');
    const centre = anchor === 'modal_center';
    el.scrollLeft = centre || !right ? (centre ? (el.scrollWidth - el.clientWidth) / 2 : 0)
      : el.scrollWidth;
    el.scrollTop = centre ? (el.scrollHeight - el.clientHeight) / 2 : bottom ? el.scrollHeight : 0;
  }, [zoom, anchor, bundle, device, view]);

  const srcDoc = useMemo(() => {
    if (!bundle) return '';
    const shown = logo && bundle.ui.logo?.url
      ? { ...bundle, ui: { ...bundle.ui, logo: { ...bundle.ui.logo, url: logo } } }
      : bundle;
    // Everything the boot script needs, in one block. `<` is escaped so a string inside
    // the bundle can never close the element early — the same reason the bundle itself was
    // escaped when it was inlined.
    const payload = JSON.stringify({
      bundle: shown,
      view,
      uiUrl: UI_BUNDLE_URL,
      strings: {
        error: t('Vorschau-Fehler: '),
        uiFailed: t('ui.js konnte nicht geladen werden.'),
      },
    }).replace(/</g, '\\u003c');
    // Absolute, not '/preview-boot.js': the frame is sandboxed without allow-same-origin,
    // so its origin is opaque and relying on how `self` resolves in an inherited policy is
    // exactly the kind of subtlety that works in one browser and not the next.
    const bootUrl = `${location.origin}/preview-boot.js`;
    return `<!doctype html><html lang="de"><head><meta charset="utf-8">
<style>
  html,body{margin:0;height:100%;font:14px/1.5 system-ui,sans-serif;color:#374151}
  .page{padding:24px}
  .bar{height:10px;border-radius:6px;background:#e5e7eb;margin:0 0 12px}
  .bar.w60{width:60%} .bar.w40{width:40%} .bar.w80{width:80%}
  .hint{position:fixed;left:12px;bottom:12px;font-size:11px;color:#9ca3af}
  .shot{position:absolute;inset:0;width:100%;height:auto;display:block}
</style></head>
<body>
  ${backdrop
    ? `<img class="shot" alt="" src="${backdrop}">`
    : `<div class="page">
    <div class="bar w60"></div><div class="bar w80"></div><div class="bar w40"></div>
    <div class="bar w80"></div><div class="bar w60"></div>
  </div>`}
  <div class="hint">${t('Vorschau — Klicks werden nicht gespeichert')}</div>
  <!-- Not an inline <script>: an about:srcdoc document inherits this page's CSP, and the
       console ships script-src 'self'. A JSON data block is never executed, so script-src
       does not govern it; the executable half is a real file with a real origin. -->
  <script type="application/json" id="cmp-preview">${payload}</script>
  <script src="${bootUrl}"></script>
</body></html>`;
  }, [bundle, view, backdrop, logo]);

  useEffect(() => setPainted(false), [srcDoc, device, view]);

  // ── Give the caret back (Doc 04 §5) ───────────────────────────────────────────────
  //
  // ui.js focus-traps its dialog the moment it mounts, which is right on a real page and
  // wrong here: this frame is rebuilt on every debounced edit, so the trap reached across
  // and pulled focus out of the field being typed into. Renaming a button meant one
  // character, a flash, and a dead keyboard — the editor looked like it was reloading.
  //
  // A sandboxed frame is a separate browsing context, so its steal arrives as a `blur` on
  // this window with document.activeElement set to the iframe. That is the only signal:
  // the iframe element itself gets no focus event.
  const returnTo = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = document.activeElement as HTMLElement | null;
    returnTo.current = el && el !== ref.current && el !== document.body ? el : null;
    // Only the trap that fires as the new document mounts is worth undoing. After that,
    // focus landing in the frame is someone clicking the preview's own buttons, and
    // yanking it back would break the one thing the preview is interactive for.
    const done = setTimeout(() => { returnTo.current = null; }, 2000);
    return () => clearTimeout(done);
  }, [srcDoc, device]);

  useEffect(() => {
    const onBlur = (): void => {
      const back = returnTo.current;
      if (!back || document.activeElement !== ref.current) return;
      returnTo.current = null;
      if (back.isConnected) back.focus();
    };
    addEventListener('blur', onBlur);
    return () => removeEventListener('blur', onBlur);
  }, []);

  if (!bundle) {
    return (
      <div ref={box} className="grid h-[520px] place-items-center rounded-[4px] border border-dashed border-[var(--color-line)] bg-[var(--color-sunken)] text-sm text-[var(--color-muted)]">
        Vorschau wird erstellt …
      </div>
    );
  }

  // The iframe is rendered at the device's real pixel width and scaled down to fit, so
  // the banner sees the viewport it would see on that device. Resizing the frame instead
  // would show a desktop layout squeezed into a phone-sized box — which is precisely the
  // thing a responsive banner must not be judged by.
  const frameH = Math.min(dim.h, 620);
  // Reset for each new document, or the next frame is shown before it has painted.
  const frameKey = srcDoc.length + ':' + device + ':' + view + ':' + (backdrop ? '1' : '0');
  const scale = zoom === 'full' ? 1 : avail > 0 ? Math.min(1, avail / dim.w) : 0;
  return (
    <div className="overflow-hidden rounded-[4px] border border-[var(--color-line)] bg-[var(--color-sunken)] p-3
                    shadow-[inset_0_1px_3px_rgba(25,23,19,.06)]">
      <div ref={box} style={{ width: '100%' }}>
      <div
        ref={scroller}
        // At 1:1 the page is wider than the column on purpose, and the scroll position is
        // put where the banner is rather than at the top-left corner of a screenshot.
        className={`relative ${zoom === 'full' ? 'overflow-auto' : ''}`}
        style={{
          width: zoom === 'full' ? '100%' : dim.w * scale,
          height: frameH * scale,
          margin: '0 auto',
        }}
      >
        <iframe
          // Keyed on the content: ui.js mounts once into its shadow root and has no
          // re-render path, so a changed draft needs a fresh document. Passing srcDoc as
          // a prop rather than assigning it imperatively keeps React as the only thing
          // touching the element.
          key={frameKey}
          ref={ref}
          srcDoc={srcDoc}
          onLoad={() => setPainted(true)}
          title={`Banner-Vorschau — ${dim.label}`}
          // Scripts yes, same-origin no: the preview runs the real SDK bundle, and it has
          // no business reaching this document.
          sandbox="allow-scripts"
          width={dim.w}
          height={frameH}
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            border: 0,
            visibility: painted ? 'visible' : 'hidden',
          }}
          className="bg-white shadow-[0_1px_2px_rgba(25,23,19,.10),0_8px_24px_rgba(25,23,19,.06)]"
        />
        {!painted && (
          <div className="absolute inset-0 grid place-items-center rounded-[2px]
                          bg-[var(--color-surface)] text-xs text-[var(--color-muted)]">
            <span className="animate-pulse">{t('Vorschau wird gerendert …')}</span>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
